import _ from "lodash";
import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { DisableOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import ProfileCascade from "UIComponents/ProfileCascade";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";

import {
  DATE_FORMATS,
  isMessagingRevempEnabled,
  getIsMessagingWidget,
} from "MessagingV2/modules/Utils";
import classes from "./UserAvatar.scss";
import { formatDate, getGlobalTimezone, isIdEqual } from "Utils";
import { getUserId } from "modules/CommonSelectors";

const getType = ({ isDeleted, isArchived, isBlocked }) => {
  if (isDeleted) return "DELETED";
  if (isArchived) return "ARCHIVED";
  if (isBlocked) return "BLOCKED";
  return "NORMAL";
};

const avatarStyles = ({ isUIRevempEnabled, type }) => {
  const style = {
    NORMAL: {
      size: "small",
      shade: "light",
      randomizeColor: true,
      shape: isUIRevempEnabled ? "square" : "circle",
    },
    ARCHIVED: {
      size: "small",
      shade: "light",
      color: "neutral",
      shape: isUIRevempEnabled ? "square" : "circle",
    },
    DELETED: {
      size: "small",
      shade: "light",
      iconType: "individual",
      shape: isUIRevempEnabled ? "square" : "circle",
    },
    BLOCKED: {
      size: "small",
      shade: "light",
      color: "neutral",
      shape: isUIRevempEnabled ? "square" : "circle",
    },
  };

  return style[type];
};

const WrapWithBrackets = ({ children }) => <>[{children}]</>;

const UserNameComponent = ({
  user = { isDeleted: false, isArchived: true },
  fullName,
  createdAt = null,
}) => {
  const { t } = useI18n(["common"]);
  const type = getType(user);

  const suffix = {
    ARCHIVED: "common:archived",
    DELETED: "common:deleted_user",
  }[type];

  const isUIRevempEnabled = isMessagingRevempEnabled();

  const { screenMode } = useChatOptions();

  const isWidget = getIsMessagingWidget({ screenMode });

  const userNameClass = classNames(classes.userName, "text-label-4");
  const userNameClassV2 = classNames(classes.userNameV2, "text-label-3", {
    [classes.userNameWidgetV2]: isWidget,
  });

  const dateClass = classNames("text-body-3", classes.date);

  return (
    <div
      className={
        isUIRevempEnabled
          ? classes.userNameContainerV2
          : classes.userNameContainer
      }
    >
      <div className={isUIRevempEnabled ? userNameClassV2 : userNameClass}>
        {fullName}
      </div>
      {suffix ? (
        <div className="text-textSubtle">
          <WrapWithBrackets>{t(suffix)}</WrapWithBrackets>
        </div>
      ) : null}
      {isUIRevempEnabled && createdAt ? (
        <div className={dateClass}>
          {formatDate({
            date: createdAt,
            format: DATE_FORMATS.SENT_MESSAGE,
          })}
        </div>
      ) : null}
    </div>
  );
};

const mapStateToPropsUserNames = () => ({
  currentTimezone: getGlobalTimezone(),
});

export const UserName = connect(mapStateToPropsUserNames)(UserNameComponent);

export const AVATAR_RENDER_MODES = {
  AVATAR: "avatar",
  SPACE: "space",
  NOTHING: "nothing",
};

const UserAvatar = ({ user, fullName, avatarRenderMode, isBlocked }) => {
  const showBlocked = isBlocked && !isIdEqual(user.id, getUserId());

  if (avatarRenderMode === AVATAR_RENDER_MODES.SPACE) {
    return <div className={classes.avatarSpace} />;
  }

  if (avatarRenderMode === AVATAR_RENDER_MODES.NOTHING) {
    return null;
  }

  const type = getType({ ...user, isBlocked: showBlocked });
  const isUIRevempEnabled = isMessagingRevempEnabled();

  const avatarItem = {
    id: user.id,
    value: fullName,
    imageUrl: user.profileImage,
  };

  return (
    <div className={classes.avatarContainer}>
      <ProfileCascade
        items={[avatarItem]}
        showAvatar
        avatarStyles={avatarStyles({ isUIRevempEnabled, type })}
      />
      {showBlocked ? (
        <div className={classes.blockedIcon}>
          <DisableOutlined size="xxxx-small" />
        </div>
      ) : null}
    </div>
  );
};

UserAvatar.propTypes = {
  user: PropTypes.object.isRequired,
  fullName: PropTypes.string.isRequired,
  avatarRenderMode: PropTypes.oneOf(_.values(AVATAR_RENDER_MODES)),
  isBlocked: PropTypes.bool,
};

UserAvatar.defaultProps = {
  avatarRenderMode: AVATAR_RENDER_MODES.AVATAR,
};

export default UserAvatar;
